import {
  type FaceTemplate,
  type RadioWithText,
  type FieldCheckResult,
  type ValidationError,
  createEmptyFaceTemplate,
  validateFaceTemplate,
  validateForSubmit,
  FACE_TEMPLATE_FIELD_META,
  OPTION_LABELS,
  GROUP_LABELS,
  ZZMZT_OPTIONS,
  MKWZT_OPTIONS,
  YTQD_OPTIONS,
  FHCD_OPTIONS,
  LXKD_OPTIONS,
  LXZT_OPTIONS,
  YSZT_OPTIONS,
  YRFYCD_OPTIONS,
  ZZMZT_TEXT_TAG,
  MKWZT_TEXT_TAG,
  YTQD_TEXT_TAG,
  FHCD_TEXT_TAG,
  YSZT_TEXT_TAG,
  YRFYCD_TEXT_TAG
} from '@/model/FaceTemplate';

// ── 返回结构 ──

export interface FaceTemplateReadResult {
  template: FaceTemplate;
  /** 全部字段完成度 0~100 */
  completeness: number;
  /** 必填字段完成度 0~100 */
  requiredCompleteness: number;
  /** 逐项检查结果 */
  fields: FieldCheckResult[];
  /** 提交验证错误（空数组=可提交） */
  submitErrors: ValidationError[];
  /** 模板控件完整性（缺控件时 valid=false） */
  integrity: TemplateIntegrityResult;
}

/** Word 模板控件完整性检查结果 */
export interface TemplateIntegrityResult {
  /** 是否包含 FaceTemplate 所需的全部控件 tag */
  valid: boolean;
  /** 缺失的控件 tag */
  missingTags: string[];
  /** 缺失项的可读说明 */
  missingLabels: string[];
  /** 汇总提示文案 */
  message: string;
}

// ── 单选 checkbox+文本 组定义 ──

interface RadioGroupWithTextDef {
  groupKey: keyof FaceTemplate;
  optionTags: readonly string[];
  textTag: string;
}

const RADIO_WITH_TEXT_GROUPS: RadioGroupWithTextDef[] = [
  { groupKey: 'zzmzt', optionTags: ZZMZT_OPTIONS, textTag: ZZMZT_TEXT_TAG },
  { groupKey: 'mkwzt', optionTags: MKWZT_OPTIONS, textTag: MKWZT_TEXT_TAG },
  { groupKey: 'ytqd', optionTags: YTQD_OPTIONS, textTag: YTQD_TEXT_TAG },
  { groupKey: 'fhcd', optionTags: FHCD_OPTIONS, textTag: FHCD_TEXT_TAG },
  { groupKey: 'yszt', optionTags: YSZT_OPTIONS, textTag: YSZT_TEXT_TAG },
  { groupKey: 'yrfycd', optionTags: YRFYCD_OPTIONS, textTag: YRFYCD_TEXT_TAG }
];

// ── 纯 checkbox 单选组定义 ──

interface RadioOnlyGroupDef {
  groupKey: keyof FaceTemplate;
  optionTags: readonly string[];
}

const RADIO_ONLY_GROUPS: RadioOnlyGroupDef[] = [
  { groupKey: 'lxkd', optionTags: LXKD_OPTIONS },
  { groupKey: 'lxzt', optionTags: LXZT_OPTIONS }
];

// ── tag → FaceTemplate 字段映射 ──

const SIMPLE_TEXT_TAGS: Record<string, keyof FaceTemplate> = {
  siteName: 'siteName',
  mileage: 'mileage',
  reportCode: 'reportCode',
  kwkd: 'kwkd',
  kwgd: 'kwgd',
  kwmj: 'kwmj',
  kwff: 'kwff',
  other: 'other'
};

const DROPDOWN_TAGS: Record<string, keyof FaceTemplate> = {
  design_level: 'designLevel',
  construct_level: 'constructLevel'
};

const IMAGE_TAGS: Record<string, keyof FaceTemplate> = {
  sumiao: 'sumiao',
  facePicture: 'facePicture'
};

const RICH_TEXT_TAGS: Record<string, keyof FaceTemplate> = {
  dzms: 'dzms',
  risk: 'risk'
};

const DATE_TAGS: Record<string, keyof FaceTemplate> = {
  datetime: 'datetime'
};

/** FaceTemplate 对应的全部 Word 控件 tag（缺一不可） */
export const REQUIRED_FACE_TEMPLATE_TAGS: readonly string[] = (() => {
  const tags = new Set<string>([
    ...Object.keys(SIMPLE_TEXT_TAGS),
    ...Object.keys(DROPDOWN_TAGS),
    ...Object.keys(IMAGE_TAGS),
    ...Object.keys(RICH_TEXT_TAGS),
    ...Object.keys(DATE_TAGS)
  ]);
  for (const group of [
    { options: ZZMZT_OPTIONS, text: ZZMZT_TEXT_TAG },
    { options: MKWZT_OPTIONS, text: MKWZT_TEXT_TAG },
    { options: YTQD_OPTIONS, text: YTQD_TEXT_TAG },
    { options: FHCD_OPTIONS, text: FHCD_TEXT_TAG },
    { options: YSZT_OPTIONS, text: YSZT_TEXT_TAG },
    { options: YRFYCD_OPTIONS, text: YRFYCD_TEXT_TAG }
  ]) {
    for (const tag of group.options) tags.add(tag);
    tags.add(group.text);
  }
  for (const tag of LXKD_OPTIONS) tags.add(tag);
  for (const tag of LXZT_OPTIONS) tags.add(tag);
  return Object.freeze([...tags]);
})();

function getTagDisplayLabel(tag: string): string {
  const fieldKey =
    SIMPLE_TEXT_TAGS[tag] ||
    DROPDOWN_TAGS[tag] ||
    IMAGE_TAGS[tag] ||
    RICH_TEXT_TAGS[tag] ||
    DATE_TAGS[tag];
  if (fieldKey) {
    const meta = FACE_TEMPLATE_FIELD_META.find((m) => m.key === fieldKey);
    return meta ? `${meta.label}（${tag}）` : tag;
  }
  if (OPTION_LABELS[tag]) {
    const groupKey = tag.split('_')[0] || '';
    const groupLabel = GROUP_LABELS[groupKey] || groupKey;
    return `${groupLabel}-${OPTION_LABELS[tag]}（${tag}）`;
  }
  if (tag.endsWith('_qt')) {
    const groupKey = tag.replace(/_qt$/, '');
    const groupLabel = GROUP_LABELS[groupKey] || groupKey;
    return `${groupLabel}-其他（${tag}）`;
  }
  return tag;
}

/**
 * 根据文档中已存在的控件 tag，检查 FaceTemplate 所需控件是否齐全。
 */
export function checkFaceTemplateControlsIntegrity(
  presentTags: Iterable<string>
): TemplateIntegrityResult {
  const present = new Set(
    [...presentTags].map((t) => t.trim()).filter((t) => t.length > 0)
  );
  const missingTags = REQUIRED_FACE_TEMPLATE_TAGS.filter((tag) => !present.has(tag));
  const missingLabels = missingTags.map(getTagDisplayLabel);
  if (missingTags.length === 0) {
    return {
      valid: true,
      missingTags: [],
      missingLabels: [],
      message: ''
    };
  }
  return {
    valid: false,
    missingTags,
    missingLabels,
    message: `Word 模板格式错误：缺少 ${missingTags.length} 个必要内容控件，请使用正确的预报模板。`
  };
}

/**
 * 从当前 Word 文档读取控件 tag，并检查模板完整性。
 */
export async function checkFaceTemplateControlsIntegrityFromDoc(): Promise<TemplateIntegrityResult> {
  return Word.run(async (context) => {
    const controls = getDocumentContentControls(context);
    controls.load('items/tag');
    await context.sync();
    const tags = controls.items.map((cc) => cc.tag || '');
    return checkFaceTemplateControlsIntegrity(tags);
  });
}

// ── 占位符过滤 ──
// Word 图片内容控件即使未插入图片，内部也有一个占位符图标的 InlinePicture，
// 其 getBase64ImageSrc() 会返回非空 base64。但占位符图标的实际图片数据很小（通常 < 4KB），
// 对应 base64 字符串长度 < 约 5500 字符。用户插入的真实照片/素描至少几十KB。
// 用 base64 长度阈值区分，仅依赖 WordApi 1.1 的 getBase64ImageSrc()。
const REAL_IMAGE_MIN_BASE64_LENGTH = 6000;

// ── Word API 辅助 ──

function getSupportedCCTypes(): Word.ContentControlType[] {
  return [
    Word.ContentControlType.richText,
    Word.ContentControlType.plainText,
    Word.ContentControlType.checkBox,
    Word.ContentControlType.dropDownList,
    Word.ContentControlType.datePicker,
    Word.ContentControlType.picture
  ];
}

/**
 * 获取文档内容控件集合。
 * getContentControls({ types }) 属于 WordApi 1.5+；本地自托管的 @microsoft/office-js
 *（word-win32-16.01.js）往往未实现该方法，需回退到 WordApi 1.1 的 contentControls。
 */
function getDocumentContentControls(context: Word.RequestContext): Word.ContentControlCollection {
  const doc = context.document as Word.Document & {
    getContentControls?: (options?: Word.ContentControlOptions) => Word.ContentControlCollection
  }
  if (typeof doc.getContentControls === 'function') {
    return doc.getContentControls({ types: getSupportedCCTypes() })
  }
  return doc.contentControls
}

/** 内容控件变更订阅句柄 */
export interface FaceTemplateControlSubscription {
  dispose: () => Promise<void>
}

/**
 * 监听 FaceTemplate 相关内容控件：
 * - onDataChanged / onExited：填报值变化（桌面端多在离开控件后触发）
 * - onDeleted：必要控件被删除（应立即做完整性检查并锁定）
 * 回调侧请对 onChange 自行 debounce；onDeleted 建议立即处理。
 */
export async function subscribeFaceTemplateControlChanges(handlers: {
  onChange: () => void
  onDeleted?: () => void
}): Promise<FaceTemplateControlSubscription> {
  type HandlerResult = OfficeExtension.EventHandlerResult<
    | Word.ContentControlDataChangedEventArgs
    | Word.ContentControlExitedEventArgs
    | Word.ContentControlDeletedEventArgs
  >
  const handlerResults: HandlerResult[] = []
  const trackedControls: Word.ContentControl[] = []
  let disposed = false

  const notifyChange = () => {
    if (!disposed) handlers.onChange()
  }
  const notifyDeleted = () => {
    if (disposed) return
    if (handlers.onDeleted) handlers.onDeleted()
    else handlers.onChange()
  }

  await Word.run(async (context) => {
    const controls = getDocumentContentControls(context)
    controls.load('items/tag')
    await context.sync()

    const requiredTags = new Set(REQUIRED_FACE_TEMPLATE_TAGS)
    for (const cc of controls.items) {
      if (!cc.tag || !requiredTags.has(cc.tag)) continue
      cc.track()
      trackedControls.push(cc)
      // handler 内勿 load 被删控件（官方要求），仅回调通知即可
      handlerResults.push(cc.onDeleted.add(async () => notifyDeleted()))
      handlerResults.push(cc.onDataChanged.add(async () => notifyChange()))
      handlerResults.push(cc.onExited.add(async () => notifyChange()))
    }
    await context.sync()
    console.log(`已注册 ${handlerResults.length} 个内容控件事件监听`)
  })

  return {
    dispose: async () => {
      disposed = true
      if (handlerResults.length === 0) return
      try {
        const anchor = trackedControls[0]
        const batch = async (context: Word.RequestContext) => {
          for (const handler of handlerResults) {
            handler.remove()
          }
          for (const cc of trackedControls) {
            cc.untrack()
          }
          await context.sync()
        }
        if (anchor) {
          await Word.run(anchor, batch)
        } else {
          await Word.run(batch)
        }
      } catch (err) {
        console.warn('移除内容控件事件监听失败', err)
      }
      handlerResults.length = 0
      trackedControls.length = 0
    }
  }
}

interface ContentControlData {
  text: string;
  type: string;
  isChecked: boolean;
  hasImage: boolean;
  imageBase64: string;
}

async function readAllContentControls(
  context: Word.RequestContext
): Promise<Map<string, ContentControlData>> {
  const controls = getDocumentContentControls(context);
  controls.load('items/tag,items/text,items/type,items/title');
  await context.sync();

  console.log('--- 控件总数:', controls.items.length, '---');
  if (controls.items.length === 0) {
    return new Map();
  }

  // 第二轮：checkbox 加载 isChecked，图片加载 inlinePictures
  const checkboxIndexes: number[] = [];
  const imageIndexes: number[] = [];
  for (let i = 0; i < controls.items.length; i++) {
    const ccType = controls.items[i]!.type;
    if (ccType === Word.ContentControlType.checkBox) {
      controls.items[i]!.load('checkboxContentControl/isChecked');
      checkboxIndexes.push(i);
    }
    if (ccType === Word.ContentControlType.picture || ccType === Word.ContentControlType.richText) {
      try {
        controls.items[i]!.inlinePictures.load('items');
        imageIndexes.push(i);
      } catch {
        // ignore
      }
    }
  }
  if (checkboxIndexes.length > 0 || imageIndexes.length > 0) {
    await context.sync();
  }

  const result = new Map<string, ContentControlData>();
  for (let i = 0; i < controls.items.length; i++) {
    const cc = controls.items[i]!;
    const isCheckbox = checkboxIndexes.includes(i);
    let checkedValue = false;
    if (isCheckbox) {
      try {
        checkedValue = !!cc.checkboxContentControl.isChecked;
      } catch {
        checkedValue = false;
      }
    }
    console.log(
      `控件 #${i + 1}: Tag="${cc.tag}", Type=${cc.type}, Text="${cc.text}"` +
      (isCheckbox ? `, isChecked=${checkedValue}` : '')
    );
    if (!cc.tag) continue;
    let hasImage = false;
    if (imageIndexes.includes(i)) {
      try {
        hasImage = cc.inlinePictures.items.length > 0;
      } catch {
        hasImage = false;
      }
    }
    result.set(cc.tag, {
      text: cc.text,
      type: cc.type,
      isChecked: checkedValue,
      hasImage,
      imageBase64: ''
    });
  }

  // 第三轮：对图片控件取 base64，通过长度判断是真实图片还是占位符
  const imageTagSet = new Set(Object.keys(IMAGE_TAGS));
  const base64Requests: { tag: string; clientResult: OfficeExtension.ClientResult<string> }[] = [];
  for (const i of imageIndexes) {
    const cc = controls.items[i]!;
    if (!cc.tag || !imageTagSet.has(cc.tag)) continue;
    try {
      if (cc.inlinePictures.items.length > 0 && cc.inlinePictures.items[0]) {
        const clientResult = cc.inlinePictures.items[0].getBase64ImageSrc();
        base64Requests.push({ tag: cc.tag, clientResult });
      }
    } catch {
      // ignore
    }
  }
  if (base64Requests.length > 0) {
    await context.sync();
    for (const { tag, clientResult } of base64Requests) {
      const base64 = clientResult.value || '';
      const isReal = base64.length >= REAL_IMAGE_MIN_BASE64_LENGTH;
      console.log(
        `图片控件 Tag="${tag}", base64长度=${base64.length}, 判定为${isReal ? '真实图片' : '占位符'}`
      );
      const data = result.get(tag);
      if (data) {
        data.hasImage = isReal;
        data.imageBase64 = isReal ? base64 : '';
      }
    }
  }

  console.log('获取doc控件集合', result.size, '个');
  return result;
}

// ── 主入口：读取文档 → FaceTemplate ──

export async function readFaceTemplateFromDoc(): Promise<FaceTemplateReadResult> {
  return Word.run(async context => {
    const ccMap = await readAllContentControls(context);
    const integrity = checkFaceTemplateControlsIntegrity(ccMap.keys());
    const template = createEmptyFaceTemplate();

    const NUMBER_FIELDS: Set<keyof FaceTemplate> = new Set(['kwkd', 'kwgd', 'kwmj']);

    // 1. 简单文本、下拉、图片、富文本、日期
    for (const [tag, data] of ccMap) {
      if (SIMPLE_TEXT_TAGS[tag]) {
        const fieldKey = SIMPLE_TEXT_TAGS[tag];
        const rawValue = data.text.trim();
        if (NUMBER_FIELDS.has(fieldKey)) {
          const num = Number(rawValue);
          (template as any)[fieldKey] = rawValue !== '' && !isNaN(num) && isFinite(num) ? num : 0;
        } else {
          (template as any)[fieldKey] = rawValue;
        }
      } else if (DROPDOWN_TAGS[tag]) {
        (template as any)[DROPDOWN_TAGS[tag]] = data.text.trim();
      } else if (IMAGE_TAGS[tag]) {
        (template as any)[IMAGE_TAGS[tag]] = data.imageBase64;
      } else if (RICH_TEXT_TAGS[tag]) {
        (template as any)[RICH_TEXT_TAGS[tag]] = data.text.trim();
      } else if (DATE_TAGS[tag]) {
        (template as any)[DATE_TAGS[tag]] = data.text.trim();
      }
    }

    // 2. 单选 checkbox+文本 组：取第一个选中的 checkbox，或取文本
    for (const groupDef of RADIO_WITH_TEXT_GROUPS) {
      let selected: string | null = null;

      for (const optionTag of groupDef.optionTags) {
        const data = ccMap.get(optionTag);
        if (data && data.isChecked) {
          selected = optionTag;
          break; // 单选，取第一个选中的
        }
      }

      let otherText = '';
      const textData = ccMap.get(groupDef.textTag);
      if (textData) {
        otherText = textData.text.trim();
      }

      (template as any)[groupDef.groupKey] = { selected, otherText } as RadioWithText;
    }

    // 3. 纯 checkbox 单选组：取第一个选中的
    for (const groupDef of RADIO_ONLY_GROUPS) {
      let selected: string | null = null;

      for (const optionTag of groupDef.optionTags) {
        const data = ccMap.get(optionTag);
        if (data && data.isChecked) {
          selected = optionTag;
          break;
        }
      }

      (template as any)[groupDef.groupKey] = selected;
    }

    // 模板不完整时仍返回空进度，避免误导
    if (!integrity.valid) {
      return {
        template,
        completeness: 0,
        requiredCompleteness: 0,
        fields: [],
        submitErrors: [],
        integrity
      };
    }

    // 4. 计算完成度
    const fields = validateFaceTemplate(template);

    const totalFields = fields.length;
    const filledFields = fields.filter(f => f.filled).length;
    const completeness = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

    const requiredFields = fields.filter(f => f.required);
    const filledRequired = requiredFields.filter(f => f.filled).length;
    const requiredCompleteness =
      requiredFields.length > 0 ? Math.round((filledRequired / requiredFields.length) * 100) : 0;

    // 5. 提交验证
    const submitErrors = validateForSubmit(template);

    return { template, completeness, requiredCompleteness, fields, submitErrors, integrity };
  });
}

// ── 获取图片 base64 ──

export function getImageBase64FromDoc(tag: string): Promise<string> {
  return Word.run(async context => {
    const controls = getDocumentContentControls(context);
    controls.load('items/tag');
    await context.sync();

    let targetCC: Word.ContentControl | null = null;
    for (const cc of controls.items) {
      if (cc.tag === tag) {
        targetCC = cc;
        break;
      }
    }
    if (!targetCC) return '';

    const inlinePictures = targetCC.inlinePictures;
    inlinePictures.load('items');
    await context.sync();

    if (inlinePictures.items.length === 0 || !inlinePictures.items[0]) return '';

    const picture = inlinePictures.items[0];
    let base64 = '';
    try {
      const base64Result = picture.getBase64ImageSrc();
      await context.sync();
      base64 = base64Result.value || '';
    } catch {
      // ignore
    }
    if (base64.length < REAL_IMAGE_MIN_BASE64_LENGTH) {
      console.log(
        `getImageBase64FromDoc Tag="${tag}", base64长度=${base64.length}, 判定为占位符，跳过`
      );
      return '';
    }
    return base64;
  });
}
